import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Ship, Loader2, Pencil, Flag, CalendarClock, Eye, EyeOff } from 'lucide-react';
import { trackingApi, bookingsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, Badge, Tag, Tabs, Card } from '../components/sv';
import { formatDate } from '../lib/utils';
import { EtaDisplay } from '../components/shared/EtaDisplay';
import { EventEditDialog } from '../components/bridge/EventTimeline';
import { RecordMilestoneDialog } from '../components/bridge/RecordMilestoneDialog';
import { EtaDialog } from '../components/bridge/EtaDialog';
import { FreeTimeCard } from '../components/shared/FreeTimeCard';

const STATUS_LABEL = { pending: 'Booking received', confirmed: 'Confirmed', in_transit: 'In transit', arrived: 'Arrived', customs: 'Customs', out_for_delivery: 'Out for delivery', delivered: 'Delivered', cancelled: 'Cancelled' };
const PROGRESS = { pending: 0.05, confirmed: 0.15, in_transit: 0.6, arrived: 0.8, customs: 0.85, out_for_delivery: 0.92, delivered: 1, cancelled: 0 };

function fmt(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return ts;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// Shape the public tracking payload like a booking so the Bridge ETA dialog can reuse it.
function asBooking(data) {
  return { id: data.booking_id, booking_number: data.booking_number, origin_port: data.origin, destination_port: data.destination, estimated_delivery: data.estimated_arrival, pickup_date: data.pickup_date };
}

export default function TrackPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAgent: staff } = useAuth();
  const [reference, setReference] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('events');
  const [editingEvent, setEditingEvent] = useState(null);
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [etaOpen, setEtaOpen] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [internalEvents, setInternalEvents] = useState(null);

  useEffect(function () {
    const ref = searchParams.get('ref');
    if (ref) { setReference(ref); doTrack(ref); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doTrack(ref) {
    const q = (ref || '').trim();
    if (!q) return;
    setLoading(true); setError('');
    try {
      const res = await trackingApi.track(q);
      setData(res.data);
      setSearchParams({ ref: q });
      if (staff && res.data.booking_id && showInternal) {
        try { const r = await bookingsApi.events(res.data.booking_id); setInternalEvents(r.data.events); } catch (err) { setInternalEvents([]); }
      } else {
        setInternalEvents(null);
      }
    } catch (e) {
      setError('No shipment found for that reference. Check the booking, container or bill of lading number and try again.');
      setData(null);
    } finally { setLoading(false); }
  }

  function refresh() { doTrack(data ? data.booking_number : reference); }

  const canEdit = staff && data && data.booking_id;

  useEffect(function () {
    if (!canEdit || !showInternal || internalEvents) return;
    bookingsApi.events(data.booking_id).then(function (r) { setInternalEvents(r.data.events); }).catch(function () { setInternalEvents([]); });
  }, [canEdit, showInternal, internalEvents, data]);

  const publicEvents = data ? (data.events || []) : [];
  const events = showInternal && internalEvents ? [...internalEvents].sort(function (a, b) { return new Date(a.timestamp) - new Date(b.timestamp); }) : publicEvents;
  const lastPublic = publicEvents.length ? publicEvents[publicEvents.length - 1] : null;
  const progress = data ? PROGRESS[data.status] ?? 0.1 : 0;
  const isBulk = data?.shipment_mode === 'bulk';
  const etaBooking = useMemo(function () { return data ? asBooking(data) : null; }, [data]);

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="track-page">
      <div className="bg-white border-b border-gray-200">
        <div className="sv-wrap py-10 md:py-12">
          <h1 className="text-[36px] md:text-[40px] sv-display">Track a shipment</h1>
          <form onSubmit={function (e) { e.preventDefault(); doTrack(reference); }} className="flex flex-col sm:flex-row gap-3 mt-6 max-w-[760px]">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-gray-600" strokeWidth={1.75} />
              <input value={reference} onChange={function (e) { setReference(e.target.value); }} placeholder="Container, booking or bill of lading number" className="sv-input sv-mono h-[52px] pl-11" data-testid="track-input" />
            </div>
            <Button size="lg" type="submit" disabled={loading} data-testid="track-submit">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Track'}</Button>
          </form>
        </div>
      </div>

      <div className="sv-wrap py-8 pb-24 grid gap-6">
        {error && (
          <Card className="max-w-[760px]" data-testid="track-error">
            <p className="text-lg">Nothing matched <span className="sv-mono">{reference}</span>.</p>
            <p className="text-gray-700 mt-2">{error}</p>
            <p className="text-sm text-gray-600 mt-4">Booking numbers look like <span className="sv-mono">SDV482911734</span>; bills of lading like <span className="sv-mono">SDVU48291173</span>; container numbers like <span className="sv-mono">SDVU1234567</span>.</p>
          </Card>
        )}

        {!data && !error && !loading && (
          <Card className="max-w-[760px]" data-testid="track-empty">
            <p className="text-lg">Enter a reference to see the latest position, milestones and ETA.</p>
            <p className="text-gray-700 mt-2">Registered customers see every active shipment in <Link to="/login" className="sv-link">Compass</Link>, the customer portal.</p>
          </Card>
        )}

        {data && (
          <>
            {canEdit && (
              <div className="flex flex-wrap items-center gap-3 px-5 py-3 bg-ink text-white rounded" data-testid="track-bridge-tools">
                <span className="text-sm text-white/80">Bridge tools — you are seeing the public view; changes show to the customer immediately.</span>
                <div className="flex-1" />
                <button type="button" onClick={function () { setShowInternal(!showInternal); }} className={"inline-flex items-center gap-2 h-9 px-4 text-sm rounded border transition-colors duration-[120ms] " + (showInternal ? 'bg-amber-400 border-amber-400 text-ink' : 'border-white/40 text-white hover:bg-white/10')} data-testid="track-toggle-internal">{showInternal ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />} {showInternal ? 'Hide internal events' : 'Show internal events'}</button>
                <button type="button" onClick={function () { setMilestoneOpen(true); }} className="inline-flex items-center gap-2 h-9 px-4 text-sm rounded bg-white text-ink hover:bg-gray-100 transition-colors duration-[120ms]" data-testid="track-record-milestone"><Flag className="h-4 w-4" strokeWidth={1.75} /> Record milestone</button>
                <button type="button" onClick={function () { setEtaOpen(true); }} className="inline-flex items-center gap-2 h-9 px-4 text-sm rounded border border-white/40 text-white hover:bg-white/10 transition-colors duration-[120ms]" data-testid="track-revise-eta"><CalendarClock className="h-4 w-4" strokeWidth={1.75} /> Revise ETA</button>
              </div>
            )}
            <Card padding="p-8" data-testid="track-summary">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm text-gray-600">Booking</span>
                <span className="sv-mono text-xl" data-testid="track-booking-number">{data.booking_number}</span>
                {data.bl_number && <span className="text-sm text-gray-600">B/L <span className="sv-mono text-ink" data-testid="track-bl-number">{data.bl_number}</span></span>}
                <Badge status={data.status} data-testid="track-status">{STATUS_LABEL[data.status] || data.status}</Badge>
                {isBulk && <Tag>Bulk cargo</Tag>}
                <div className="flex-1" />
                <Button variant="secondary" size="sm" onClick={function () { navigator.clipboard && navigator.clipboard.writeText(window.location.href); }} data-testid="track-share">Copy link</Button>
              </div>
              <div className="grid md:grid-cols-[1fr_auto_1fr] gap-8 items-end mt-10">
                <div>
                  <div className="text-sm text-gray-600">From</div>
                  <div className="text-[28px] md:text-[32px] sv-display mt-1" data-testid="track-origin">{data.origin}</div>
                  <div className="sv-mono text-sm text-gray-700 mt-1">{data.pickup_date ? 'cargo ready ' + formatDate(data.pickup_date) : 'origin port'}</div>
                </div>
                <div className="text-center md:pb-3">
                  <div className="text-sm text-gray-600">{data.vessel_name}{data.voyage_number ? ' · ' + data.voyage_number : ''}</div>
                  <div className="text-sm text-gray-700 mt-1">Now: {data.current_location}</div>
                </div>
                <div className="md:text-right">
                  <div className="text-sm text-gray-600">To</div>
                  <div className="text-[28px] md:text-[32px] sv-display mt-1" data-testid="track-destination">{data.destination}</div>
                  <div className="text-sm text-gray-700 mt-1"><EtaDisplay booking={data} prefix="ETA " className="md:justify-end" testId="track-eta" /></div>
                </div>
              </div>
              <div className="relative h-1 bg-gray-200 rounded mt-6">
                <div className="absolute inset-y-0 left-0 bg-ink rounded" style={{ width: (progress * 100) + '%' }} />
                <div className="absolute left-0 -top-1 w-3 h-3 rounded-full bg-ink" />
                <div className="absolute right-0 -top-1 w-3 h-3 rounded-full bg-white border-2 border-gray-400" />
                <div className="absolute -top-3.5 w-8 h-8 rounded-full bg-blue-500 text-ink grid place-items-center" style={{ left: 'calc(' + (progress * 100) + '% - 16px)' }}><Ship className="h-4 w-4" strokeWidth={1.75} /></div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10 pt-6 border-t border-gray-200">
                {[
                  [isBulk ? 'Cargo' : 'Containers', isBulk ? Number(data.quantity_mt || 0).toLocaleString() + ' MT ' + String(data.bulk_commodity || '').replace(/_/g, ' ') : (data.container_numbers?.length ? data.container_numbers.length + ' assigned' : (data.container_count || 0) + ' × ' + (data.container_type || ''))],
                  ['Last event', lastPublic ? lastPublic.status + (lastPublic.location ? ' · ' + lastPublic.location : '') : 'Booking received'],
                  ['Current location', data.current_location],
                  ['Commodity', data.commodity || '—'],
                ].map(function (r) { return <div key={r[0]}><div className="text-sm text-gray-600">{r[0]}</div><div className="mt-1">{r[1]}</div></div>; })}
              </div>
            </Card>

            <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6 items-start">
              <div className="sv-card">
                <div className="px-8 pt-1"><Tabs active={tab} onChange={setTab} tabs={[{ id: 'events', label: 'Events', count: events.length }, { id: 'containers', label: isBulk ? 'Cargo' : 'Containers', count: isBulk ? undefined : (data.container_numbers || []).length }, { id: 'docs', label: 'Documents' }]} /></div>
                {tab === 'events' && (
                  <div className="px-8 pt-6 pb-2" data-testid="track-events">
                    {events.length === 0 && <p className="text-gray-600 pb-6">No milestones yet. Container movements appear here once the booking is confirmed.</p>}
                    {events.map(function (ev, i) {
                      const last = i === events.length - 1;
                      const internal = showInternal && ev.public === false;
                      const label = ev.status;
                      const detail = showInternal && ev.description && ev.description !== ev.status ? ev.description : null;
                      return (
                        <div key={ev.index} className={"grid grid-cols-[24px_1fr_auto] gap-4 group" + (internal ? ' opacity-70' : '')} data-testid={"track-event-" + i}>
                          <div className="flex flex-col items-center">
                            <div className={"w-3 h-3 rounded-full mt-1.5 " + (internal ? 'border-2 border-gray-400 bg-white' : (last ? 'bg-blue-500' : 'bg-ink'))} />
                            {!last && <div className="w-0.5 flex-1 min-h-[28px] bg-ink" />}
                          </div>
                          <div className="pb-5">
                            <div className={last && !internal ? 'font-medium' : ''} data-testid={"track-event-label-" + i}>{label}{internal && <span className="ml-2 text-[11px] font-normal text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded-full align-middle" data-testid={"track-event-internal-" + i}>internal</span>}{canEdit && ev.edited_at && <span className="ml-2 text-[11px] font-normal text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded-full align-middle" title={'Edited by ' + ev.edited_by}>edited</span>}{canEdit && ev.backdated && <span className="ml-2 text-[11px] font-normal text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded-full align-middle">backdated</span>}</div>
                            <div className="text-[15px] text-gray-700">{ev.location}{ev.vessel ? <span className="sv-mono text-[13px] text-gray-600"> · {ev.vessel}{ev.voyage ? ' / ' + ev.voyage : ''}</span> : null}</div>
                            {detail && <div className="text-[13px] text-gray-500 mt-0.5">{detail}</div>}
                          </div>
                          <div className="flex items-start gap-2 pt-0.5">
                            <span className="sv-mono text-[13px] text-gray-600 whitespace-nowrap" data-testid={"track-event-time-" + i}>{fmt(ev.timestamp)}</span>
                            {canEdit && <button type="button" onClick={function () { setEditingEvent(ev); }} className="h-6 w-6 -mt-1 grid place-items-center rounded text-gray-500 hover:text-ink hover:bg-gray-100 transition-colors duration-[120ms]" title="Adjust date, time or wording" data-testid={"track-event-edit-" + ev.index}><Pencil className="h-3.5 w-3.5" /></button>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {tab === 'containers' && (
                  isBulk ? (
                    <div className="p-8 grid sm:grid-cols-3 gap-6 text-[15px]">
                      {[['Commodity', String(data.bulk_commodity || '').replace(/_/g, ' ')], ['Quantity', Number(data.quantity_mt || 0).toLocaleString() + ' MT'], ['Vessel', data.vessel_name]].map(function (r) { return <div key={r[0]}><div className="text-sm text-gray-600">{r[0]}</div><div className="mt-1 capitalize">{r[1]}</div></div>; })}
                    </div>
                  ) : (
                    <table className="sv-table" data-testid="track-containers">
                      <thead><tr><th>Container</th><th>Seal</th><th>Type</th><th>Gross</th><th>Location</th><th>Status</th></tr></thead>
                      <tbody>
                        {(data.container_numbers || []).length === 0 && <tr><td colSpan="6" className="text-gray-600">Containers are assigned once the booking is confirmed.</td></tr>}
                        {(data.container_numbers || []).map(function (c) {
                          const rec = (data.containers || []).find(function (x) { return x.number === c; }) || {};
                          return <tr key={c} data-testid={'track-container-' + c}><td className="sv-mono text-sm">{c}</td><td className="sv-mono text-sm">{rec.seal_number || '—'}</td><td><Tag mono>{rec.size ? rec.size + ' ' + rec.type : data.container_type}</Tag></td><td className="sv-mono text-sm">{rec.gross_weight_kg ? Number(rec.gross_weight_kg).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' kg' : '—'}</td><td>{data.current_location}</td><td><Badge status={data.status}>{STATUS_LABEL[data.status] || data.status}</Badge></td></tr>;
                        })}
                      </tbody>
                    </table>
                  )
                )}
                {tab === 'docs' && <div className="p-8 text-gray-600 text-[15px]">Documents are available to the booking party after <Link to="/login" className="sv-link">log in</Link>.</div>}
              </div>

              <div className="grid gap-6">
                <FreeTimeCard bookingId={data.booking_id} publicData={data.free_time} className="sv-card shadow-none" testId="track-free-time" />
                <div className="sv-card overflow-hidden">
                  <div className="h-[220px] bg-gray-200"><img src="/img/gantry-cranes.jpg" alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-5 flex items-center gap-3 text-[15px]"><Ship className="h-[18px] w-[18px]" strokeWidth={1.75} /><span className="flex-1">{data.vessel_name}{data.voyage_number ? ' · ' + data.voyage_number : ''}</span><span className="sv-mono text-[13px] text-gray-600">{data.current_location}</span></div>
                </div>
                {!staff && (
                  <Card title="Stay informed">
                    <p className="text-[15px] text-gray-700 mb-4">Registered customers receive an email when the ETA changes or the cargo is available for pickup.</p>
                    <div className="flex gap-2"><Button size="sm" to="/register">Open an account</Button><Button variant="ghost" size="sm" to="/login">Log in</Button></div>
                  </Card>
                )}
              </div>
            </div>

            {canEdit && (
              <>
                <EventEditDialog bookingId={data.booking_id} event={editingEvent} onOpenChange={function (o) { if (!o) setEditingEvent(null); }} onSaved={refresh} />
                <RecordMilestoneDialog bookingId={data.booking_id} bookingNumber={data.booking_number} open={milestoneOpen} onOpenChange={setMilestoneOpen} onSaved={refresh} />
                <EtaDialog booking={etaBooking} open={etaOpen} onOpenChange={setEtaOpen} onUpdated={refresh} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
